/**
 * @hump/shared-types
 *
 * 前后端共享的类型定义。单一权威源。
 *
 * - Hump Doc 领域模型：手写（对齐 architecture.md）
 * - API DTO 类型：v1.1 后从 OpenAPI spec 自动生成（src/generated/）
 */

// ============================================================================
// Hump Doc 格式（对齐 architecture.md §二）
// ============================================================================

/** 格式版本 */
export const HUMP_DOC_VERSION = '1.0' as const;

/** Block 类型 */
export type BlockType =
  | 'heading'
  | 'paragraph'
  | 'list'
  | 'code'
  | 'image'
  | 'quote'
  | 'formula'
  | 'table';

/** 翻译状态 */
export type TransStatus = 'none' | 'pending' | 'done';

/** 文档索引（index.hump） */
export interface DocIndex {
  version: string;
  id: string;
  meta: {
    title: string;
    author?: string;
    sourceType: SourceType;
    source: string;
    importedAt: string;
    updatedAt: string;
    progress: number;
    lang: string;
    route?: string;
    tags: string[];
  };
  blockIds: string[];
}

/** 来源类型 */
export type SourceType = 'pdf' | 'epub' | 'md' | 'docx' | 'url';

/** 单个 Block */
export interface Block {
  id: string;
  docId: string;
  type: BlockType;
  level?: number;
  order: number;
  original: string;
  translation: string;
  transStatus: TransStatus;
  updatedAt: string;
  annotations: Annotation[];
  vocab: string[];
  /** image block: data URL 或文件引用 */
  imageData?: string;
  /** image block: 宽高 */
  imageWidth?: number;
  imageHeight?: number;
  /** image block: 图片标题（"Figure 1: ..."） */
  caption?: string;
  /** formula block: LaTeX 源码 */
  latex?: string;
  /** table block: 结构化行列数据 */
  tableData?: { headers: string[]; rows: string[][] };
}

/** 批注 */
export interface Annotation {
  id: string;
  text: string;
  range?: [number, number];
  createdAt: string;
}

// ============================================================================
// 生词本（vocab/hump.json，对齐 architecture.md §四）
// ============================================================================

/** SRS 状态 */
export type SrsState = 'new' | 'learning' | 'review' | 'relearning';

/** SRS 调度数据 */
export interface SrsData {
  algorithm: 'fsrs';
  stability: number;
  difficulty: number;
  due: string;
  lastReview: string | null;
  reps: number;
  lapses: number;
  state: SrsState;
}

/** 生词 */
export interface VocabWord {
  id: string;
  word: string;
  phonetic?: string;
  pos?: string;
  definitions: string[];
  examples: VocabExample[];
  sources: VocabSource[];
  srs?: SrsData;
}

/** 生词例句 */
export interface VocabExample {
  text: string;
  docId?: string;
  blockId?: string;
  docDeleted?: boolean;
}

/** 生词来源追溯 */
export interface VocabSource {
  docId: string;
  blockId?: string;
  addedAt: string;
  docDeleted?: boolean;
}

/** 全局生词本 */
export interface Vocab {
  version: string;
  words: VocabWord[];
}

// ============================================================================
// 知识点（Hump 驼峰统一模型）
// 驼峰 = 生词 + 段落收藏 + 批注 + 后续类型的统一集合
// ============================================================================

/** 知识点类型 */
export type KnowledgeType = 'word' | 'snippet' | 'note';

/** 知识点（驼峰卡片） */
export interface KnowledgeItem {
  id: string;
  type: KnowledgeType;
  /** 创建时间 */
  createdAt: string;
  /** 更新时间 */
  updatedAt: string;
  /** 来源文档 ID */
  docId: string;
  /** 来源 block ID */
  blockId?: string;
  /** 来源文档标题（冗余，便于卡片展示） */
  docTitle?: string;

  // word 类型字段
  word?: string;
  phonetic?: string;
  pos?: string;
  definitions?: string[];

  // snippet 类型字段
  /** 收藏的原文内容 */
  text?: string;
  /** 收藏时的翻译（如有） */
  translation?: string;

  // note 类型字段
  /** 批注内容 */
  note?: string;

  /** 来源上下文（原文片段，所有类型通用） */
  context?: string;
}

/** 知识点集合 */
export interface KnowledgeBase {
  version: string;
  items: KnowledgeItem[];
}

// ============================================================================
// 资料库索引（library.json，对齐 architecture.md §三）
// ============================================================================

/** 文档元数据（library.json 中） */
export interface LibraryDocument {
  id: string;
  title: string;
  sourceType: SourceType;
  addedAt: string;
  route?: string;
  tags: string[];
  progress: number;
  lastReadBlock?: string;
}

/** 资料库索引 */
export interface Library {
  version: string;
  rootDir: string;
  documents: LibraryDocument[];
  routes: string[];
  settings: {
    defaultLang: string;
    theme: 'light' | 'dark' | 'auto';
  };
}

// ============================================================================
// AI 对话（docs/{doc-id}/chat.json，对齐 architecture.md §二）
// ============================================================================

/** 对话消息 */
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  contextBlockId?: string;
  createdAt: string;
}

/** 文档级对话历史 */
export interface DocChat {
  docId: string;
  messages: ChatMessage[];
}

// ============================================================================
// AI Provider 接口（对齐 api-unified.md §二）
// ============================================================================

/** 翻译选项 */
export interface TranslateOpts {
  sourceLang: string;
  targetLang: string;
  docId?: string;
  model?: string;
}

/** Block 上下文（AI chat 注入用） */
export interface BlockContext {
  docId: string;
  blockId: string;
  original: string;
  translation?: string;
}

/** AI Provider 抽象 */
export interface AiProvider {
  translate(text: string, opts: TranslateOpts): Promise<string>;
  chat(messages: ChatMessage[], context?: BlockContext): Promise<string>;
  summarize(docId: string): Promise<string>;
}

// ============================================================================
// 用户与权益（对齐 api-contract.md /auth/me，v1.1 自动生成替代）
// ============================================================================

/** 用户层级 */
export type Tier = 'free' | 'pro';

/** 用户信息 */
export interface User {
  id: string;
  email: string;
  displayName?: string;
  tier: Tier;
  proUntil?: string;
  createdAt: string;
}

/** 权益 */
export interface Entitlements {
  aiGateway: boolean;
  voiceClone: boolean;
  fullSync: boolean;
  historyVersion: boolean;
}

/** 用量 */
export interface Usage {
  aiTokensUsed: number;
  aiTokensLimit: number | null;
  syncSpaceUsed: number;
  syncSpaceLimit: number | null;
  voiceModels: number;
  voiceModelLimit: number;
}

// ============================================================================
// 朗读 / TTS（对齐 api-unified.md §二 VoiceProvider）
// ============================================================================

/** TTS 选项 */
export interface TtsOpts {
  voiceId: string;
  rate?: number;
  pitch?: number;
  onBoundary?: (charIndex: number) => void;
}

/** 音色 */
export interface Voice {
  id: string;
  name: string;
  type: 'system' | 'cloned';
  language?: string;
  previewUrl?: string;
}

/** Voice Provider 抽象 */
export interface VoiceProvider {
  synthesize(text: string, opts: TtsOpts): Promise<ArrayBuffer>;
  listVoices(): Promise<Voice[]>;
}
